import {
  ApiError,
  createSender,
  getSenderPublicKey,
  listPgpKeys,
  listSenders,
  patchSender,
  type PgpKey,
  type PgpKeyring,
  type Sender,
  type SenderWrite,
} from "@/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { formatDateTime } from "@/lib/format";
import { FormEvent, useEffect, useState } from "react";

const EMPTY_FORM: SenderWrite = {
  display_name: "",
  header_from: "",
  envelope_from: "",
  reply_to: null,
  text_signature: "",
  dkim_domain: "",
  pgp_sign_key: null,
  copy_to: null,
};

function keyLabel(key: PgpKey): string {
  const uid = key.uids[0] ?? "ohne Benutzerkennung";
  return `${uid} · …${key.fingerprint.slice(-16)}`;
}

function keyProblem(key: PgpKey | undefined): string | null {
  if (!key) return "nicht im Schlüsselbund";
  if (!key.has_secret) return "nur öffentlicher Schlüssel";
  if (key.status === "expired") return "abgelaufen";
  if (key.status === "revoked") return "widerrufen";
  return null;
}

function PgpField({
  keyring,
  value,
  onChange,
}: {
  keyring: PgpKeyring | null;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  if (!keyring) return <p className="text-xs text-muted">Schlüsselbund wird geladen…</p>;
  const usable = keyring.keys.filter((key) => key.has_secret && key.status === "good");
  const current = value ? keyring.keys.find((key) => key.fingerprint === value) : undefined;
  const problem = value ? keyProblem(current) : null;
  return (
    <div className="space-y-1">
      <select
        id="pgp_sign_key"
        className="field"
        value={value ?? ""}
        disabled={!keyring.configured && !value}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">Nicht signieren</option>
        {usable.map((key) => (
          <option key={key.fingerprint} value={key.fingerprint}>
            {keyLabel(key)}
          </option>
        ))}
        {value && problem ? <option value={value}>…{value.slice(-16)} ({problem})</option> : null}
      </select>
      {!keyring.configured ? (
        <p className="text-[11px] text-muted">
          PGP ist nicht eingerichtet (EDICTA_PGP_GNUPGHOME, siehe docs/deployment.md).
        </p>
      ) : keyring.error ? (
        <p className="text-[11px] text-danger">{keyring.error}</p>
      ) : usable.length === 0 ? (
        <p className="text-[11px] text-muted">Kein geheimer, gültiger Schlüssel im Schlüsselbund.</p>
      ) : null}
      {problem ? (
        <p className="text-[11px] text-danger">
          Dieser Schlüssel ist {problem}. Mails dieses Absenders schlagen fehl, bis ein gültiger
          Schlüssel gewählt oder die Signatur abgeschaltet ist.
        </p>
      ) : null}
      {current && !problem ? (
        <p className="font-mono text-[11px] text-muted">
          {current.fingerprint.replace(/(.{4})/g, "$1 ").trim()}
          {current.expires ? ` · gültig bis ${formatDateTime(current.expires)}` : " · ohne Ablaufdatum"}
        </p>
      ) : null}
      <p className="text-[11px] text-muted">
        Signierte Mails gehen als PGP/MIME raus (Anhang signature.asc). Klappt das Signieren nicht,
        wird die Mail nicht unsigniert verschickt, sondern schlägt fehl.
      </p>
    </div>
  );
}

function PublicKey({ senderId }: { senderId: string }) {
  const [armored, setArmored] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function show() {
    try {
      setArmored(await getSenderPublicKey(senderId));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Öffentlicher Schlüssel nicht verfügbar.");
    }
  }

  async function copy() {
    if (!armored) return;
    try {
      await navigator.clipboard.writeText(armored);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  if (!armored) {
    return (
      <div className="space-y-1">
        <Button type="button" size="sm" variant="ghost" onClick={() => void show()}>
          Öffentlichen Schlüssel anzeigen
        </Button>
        {error ? <p className="text-[11px] text-danger">{error}</p> : null}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <textarea
        id="pgp_public_key"
        readOnly
        className="field min-h-[120px] font-mono text-[11px]"
        value={armored}
        onFocus={(e) => e.target.select()}
      />
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" onClick={() => void copy()}>
          Kopieren
        </Button>
        {copied ? <span className="text-[11px] text-muted">Kopiert.</span> : null}
        <span className="text-[11px] text-muted">
          Auf der Webseite oder per WKD veröffentlichen, sonst kann niemand die Signatur prüfen.
        </span>
      </div>
    </div>
  );
}

export function SendersPage() {
  const [senders, setSenders] = useState<Sender[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<SenderWrite>(EMPTY_FORM);
  const [replyTo, setReplyTo] = useState("");
  const [copyTo, setCopyTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [dkimTouched, setDkimTouched] = useState(false);
  const [keyring, setKeyring] = useState<PgpKeyring | null>(null);

  function load() {
    listSenders()
      .then(setSenders)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Absender konnten nicht geladen werden.");
      });
  }

  useEffect(() => {
    load();
  }, []);

  function loadKeyring() {
    listPgpKeys()
      .then(setKeyring)
      .catch(() => setKeyring({ configured: true, error: "Schlüsselbund nicht lesbar.", keys: [] }));
  }

  function startCreate() {
    setEditingId("new");
    setForm(EMPTY_FORM);
    setReplyTo("");
    setCopyTo("");
    setDkimTouched(false);
    setError(null);
    loadKeyring();
  }

  function startEdit(sender: Sender) {
    setEditingId(sender.id);
    setForm({
      display_name: sender.display_name,
      header_from: sender.header_from,
      envelope_from: sender.envelope_from,
      reply_to: sender.reply_to,
      text_signature: sender.text_signature,
      dkim_domain: sender.dkim_domain,
      pgp_sign_key: sender.pgp_sign_key,
      copy_to: sender.copy_to,
    });
    setReplyTo(sender.reply_to ?? "");
    setCopyTo(sender.copy_to ?? "");
    setDkimTouched(true);
    setError(null);
    loadKeyring();
  }

  function domainPart(email: string): string {
    const at = email.lastIndexOf("@");
    return at >= 0 ? email.slice(at + 1) : "";
  }

  function onEnvelopeFromChange(value: string) {
    setForm((prev) => ({
      ...prev,
      envelope_from: value,
      dkim_domain: dkimTouched ? prev.dkim_domain : domainPart(value),
    }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const body: SenderWrite = {
      ...form,
      reply_to: replyTo.trim() || null,
      copy_to: copyTo.trim() || null,
    };
    try {
      if (editingId === "new") {
        await createSender(body);
      } else if (editingId) {
        await patchSender(editingId, body);
      }
      setEditingId(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Absender konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-base font-semibold">Absender</h1>
        <Button variant="primary" size="sm" onClick={startCreate}>
          Neuer Absender
        </Button>
      </div>
      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}
      {senders === null && !error ? (
        <div className="h-24 animate-pulse rounded-md bg-surface-subtle" />
      ) : null}
      {senders && senders.length === 0 && editingId !== "new" ? (
        <p className="mb-3 text-sm text-muted">Keine Absender-Identitäten.</p>
      ) : null}
      {senders && senders.length > 0 ? (
        <ul className="mb-4 divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-surface">
          {senders.map((sender) => (
            <li key={sender.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{sender.display_name}</div>
                <div className="truncate font-mono text-[11px] text-muted">{sender.header_from}</div>
              </div>
              {sender.pgp_sign_key ? <Badge tone="success">PGP-signiert</Badge> : null}
              <div className="hidden text-[11px] text-muted sm:block">{sender.dkim_domain}</div>
              <Button size="sm" variant="ghost" onClick={() => startEdit(sender)}>
                Bearbeiten
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {editingId ? (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
          <h2 className="text-sm font-semibold">{editingId === "new" ? "Neuer Absender" : "Absender bearbeiten"}</h2>
          <div className="grid gap-2 md:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="display_name">
                Anzeigename
              </label>
              <input
                id="display_name"
                className="field"
                value={form.display_name}
                onChange={(e) => setForm((prev) => ({ ...prev, display_name: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="field-label" htmlFor="dkim_domain">
                DKIM-Domain
              </label>
              <input
                id="dkim_domain"
                className="field"
                value={form.dkim_domain}
                onChange={(e) => {
                  setDkimTouched(true);
                  setForm((prev) => ({ ...prev, dkim_domain: e.target.value }));
                }}
                required
              />
            </div>
            <div>
              <label className="field-label" htmlFor="header_from">
                Header-From
              </label>
              <input
                id="header_from"
                type="email"
                className="field"
                value={form.header_from}
                onChange={(e) => setForm((prev) => ({ ...prev, header_from: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="field-label" htmlFor="envelope_from">
                Envelope-From
              </label>
              <input
                id="envelope_from"
                type="email"
                className="field"
                value={form.envelope_from}
                onChange={(e) => onEnvelopeFromChange(e.target.value)}
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="field-label" htmlFor="reply_to">
                Reply-To (optional)
              </label>
              <input
                id="reply_to"
                type="email"
                className="field"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="field-label" htmlFor="copy_to">
                Kopie an (optional)
              </label>
              <input
                id="copy_to"
                type="email"
                className="field"
                value={copyTo}
                onChange={(e) => setCopyTo(e.target.value)}
              />
              <p className="mt-1 text-[11px] text-muted">
                Jeder Versand geht zusätzlich einmal an diese Adresse, z. B. in einen Archivordner.
                Testmails nicht. Die Kopie zählt nicht als Empfänger.
              </p>
            </div>
            <div>
              <label className="field-label" htmlFor="text_signature">
                Text-Signatur
              </label>
              <textarea
                id="text_signature"
                className="field min-h-[80px] font-mono text-xs"
                value={form.text_signature}
                onChange={(e) => setForm((prev) => ({ ...prev, text_signature: e.target.value }))}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="field-label" htmlFor="pgp_sign_key">
                PGP-Signatur
              </label>
              <PgpField
                keyring={keyring}
                value={form.pgp_sign_key}
                onChange={(value) => setForm((prev) => ({ ...prev, pgp_sign_key: value }))}
              />
              {editingId !== "new" &&
              form.pgp_sign_key &&
              senders?.find((sender) => sender.id === editingId)?.pgp_sign_key === form.pgp_sign_key ? (
                <PublicKey key={editingId} senderId={editingId} />
              ) : null}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setEditingId(null)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
